import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_CONFIGURATION,
  updateNonFiscalDisplay,
  updateOrderingCatalogFilter,
  updateOrderingColorBy,
  updateOrderingGuestCount,
  updateOrderingQuickClose,
  updateOrderingSharedNumbering,
  updateOrderingSuccessNotifications,
  updateOrderingSearchField,
  updateOrderingSearchScope,
  updateOrderingTableOrder,
  updateUserConfiguration,
  updateWorkingHours,
} from "../api/configuration";
import type {
  CatalogFilter,
  ColorBy,
  Configuration,
  NonFiscalDisplayMode,
  OrderingGuestCount,
  OrderingSearchScope,
  OrderingTableOrder,
  UserConfigurationUpdate,
  WorkingDay,
} from "../api/configuration";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { patchConfiguration } from "../store/authSlice";
import {
  patchSharedAccountConfiguration,
  useIsSharedTerminalWithoutEmployee,
} from "./useSharedAccountInfo";

/**
 * Настройките (на ресторанта + личните) от `auth.user.configuration` — идват
 * с `GET /user/info` при вход, refresh и ПИН на общ терминал. `configuration`
 * е винаги дефинирано: default-ите се връщат, докато user info не е зареден.
 */
export const useConfiguration = () => {
  const configuration = useAppSelector((s) => s.auth.user?.configuration);
  return {
    configuration: configuration ?? DEFAULT_CONFIGURATION,
    /** Има ли реално заредени стойности (а не default-и). */
    isLoaded: configuration !== undefined,
  };
};

// Всяка мутация връща частта, която е записала, и я слива в store-а — така
// всички екрани в този таб виждат промяната веднага, без нов /user/info.
const useConfigurationMutation = <TVariables, TResult>(
  request: (variables: TVariables) => Promise<TResult>,
  toPatch: (result: TResult) => Partial<Configuration>,
) => {
  const dispatch = useAppDispatch();
  return useMutation({
    mutationFn: request,
    onSuccess: (result) => {
      dispatch(patchConfiguration(toPatch(result)));
    },
  });
};

export const useUpdateNonFiscalDisplay = () =>
  useConfigurationMutation(
    (mode: NonFiscalDisplayMode) => updateNonFiscalDisplay(mode),
    (result) => ({ nonFiscalDisplayMode: result.mode }),
  );

export const useUpdateOrderingQuickClose = () =>
  useConfigurationMutation(
    (enabled: boolean) => updateOrderingQuickClose(enabled),
    (result) => ({ orderingQuickClose: result.enabled }),
  );

export const useUpdateOrderingSharedNumbering = () =>
  useConfigurationMutation(
    (enabled: boolean) => updateOrderingSharedNumbering(enabled),
    (result) => ({ orderingSharedNumbering: result.enabled }),
  );

export const useUpdateOrderingTableOrder = () =>
  useConfigurationMutation(
    (order: OrderingTableOrder) => updateOrderingTableOrder(order),
    (result) => ({ orderingTableOrder: result.order }),
  );

export const useUpdateOrderingSuccessNotifications = () =>
  useConfigurationMutation(
    (enabled: boolean) => updateOrderingSuccessNotifications(enabled),
    (result) => ({ orderingSuccessNotifications: result.enabled }),
  );

export const useUpdateOrderingSearchField = () =>
  useConfigurationMutation(
    (enabled: boolean) => updateOrderingSearchField(enabled),
    (result) => ({ orderingSearchField: result.enabled }),
  );

export const useUpdateOrderingSearchScope = () =>
  useConfigurationMutation(
    (scope: OrderingSearchScope) => updateOrderingSearchScope(scope),
    (result) => ({ orderingSearchScope: result.scope }),
  );

export const useUpdateOrderingGuestCount = () =>
  useConfigurationMutation(
    (mode: OrderingGuestCount) => updateOrderingGuestCount(mode),
    (result) => ({ orderingGuestCount: result.mode }),
  );

export const useUpdateOrderingCatalogFilter = () =>
  useConfigurationMutation(
    (catalogFilter: CatalogFilter) => updateOrderingCatalogFilter(catalogFilter),
    (result) => ({ catalogFilter: result.catalogFilter }),
  );

export const useUpdateOrderingColorBy = () =>
  useConfigurationMutation(
    (colorBy: ColorBy) => updateOrderingColorBy(colorBy),
    (result) => ({ colorBy: result.colorBy }),
  );

export const useUpdateWorkingHours = () =>
  useConfigurationMutation(
    (workingHours: WorkingDay[]) => updateWorkingHours(workingHours),
    (result) => ({ workingHours: result.workingHours }),
  );

const sentFieldsOf = (
  data: UserConfigurationUpdate,
  source: Partial<Configuration>,
): UserConfigurationUpdate => {
  const picked: UserConfigurationUpdate = {};
  for (const key of Object.keys(data) as (keyof UserConfigurationUpdate)[]) {
    Object.assign(picked, { [key]: source[key] });
  }
  return picked;
};

/**
 * Личните настройки се сменят от бутоните в самото „Маркиране" (Плочки/
 * Таблица, ъгълчето „Всички", БДС|Фонетична|ABC, АБВ/КОД) и трябва да
 * реагират веднага: store-ът се обновява ОПТИМИСТИЧНО още при клика, а при
 * грешка се връща старата стойност. От отговора на PUT-а (пълният набор
 * лични настройки) се сливат САМО изпратените полета — иначе два бързи клика
 * по различни бутони биха върнали за миг втория, докато първият отговор пътува.
 *
 * На ПИН екрана на общ терминал (без служител) PUT-ът тръгва с терминалния
 * токен и записва настройките на ОБЩИЯ акаунт — тогава резултатът се слива и
 * в кеша на неговия `/user/info` (useSharedAccountInfo), откъдето
 * UserConfigurationSync чете темата на ПИН екрана.
 */
export const useUpdateUserConfiguration = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const sharedTerminalWithoutEmployee = useIsSharedTerminalWithoutEmployee();
  const { configuration } = useConfiguration();
  return useMutation({
    mutationFn: (data: UserConfigurationUpdate) => updateUserConfiguration(data),
    onMutate: (data) => {
      const previous = sentFieldsOf(data, configuration);
      dispatch(patchConfiguration(data));
      return previous;
    },
    onError: (_error, _data, previous) => {
      if (previous) dispatch(patchConfiguration(previous));
    },
    onSuccess: (result, data) => {
      const saved = sentFieldsOf(data, result);
      dispatch(patchConfiguration(saved));
      if (sharedTerminalWithoutEmployee)
        patchSharedAccountConfiguration(queryClient, saved);
    },
  });
};
